import type { LogLine, LogCluster, Service } from "@coremonit/types";
import { makeRng, int, pick } from "./rng";

const TEMPLATES: Array<{ level: LogLine["level"]; template: string }> = [
  { level: "info",  template: "request completed method=GET path=/api/orders status=200 duration_ms=%d" },
  { level: "info",  template: "cache hit key=user:%d region=%s" },
  { level: "warn",  template: "slow query detected duration_ms=%d query='SELECT * FROM orders WHERE user_id=?'" },
  { level: "warn",  template: "connection pool near capacity active=%d max=32" },
  { level: "error", template: "upstream call failed peer=%s status=502 attempt=%d" },
  { level: "error", template: "unhandled exception: NullReferenceException at OrderService.finalize line %d" },
  { level: "info",  template: "starting graceful shutdown signal=SIGTERM" },
  { level: "debug", template: "trace parent=%s span=%s duration_ms=%d" },
  { level: "info",  template: "kafka consumer lag=%d topic=orders.events partition=%d" },
  { level: "fatal", template: "OOM killed by kernel — restarting container attempts=%d" },
];

const REGIONS = ["us-east-1", "eu-west-1", "ap-south-1"];

function render(template: string, rng: () => number): string {
  let i = 0;
  return template.replace(/%[ds]/g, (m) => {
    i++;
    if (m === "%d") return String(int(rng, 1, i === 1 ? 800 : 3200));
    return pick(rng, [...REGIONS, "payment-service", "auth-service", "user-service"]);
  });
}

export function generateLogs(services: Service[], seed = 42): LogLine[] {
  const rng = makeRng(seed);
  const out: LogLine[] = [];
  for (let i = 0; i < 400; i++) {
    const svc = pick(rng, services);
    const t = pick(rng, TEMPLATES);
    out.push({
      id: `log_${i}`,
      ts: new Date(Date.now() - int(rng, 5, 3600) * 1000).toISOString(),
      service: svc.name,
      pod: `${svc.name}-${Math.floor(rng() * 90 + 10)}`,
      level: t.level,
      message: render(t.template, rng),
      traceId: rng() < 0.3 ? `tr_${Date.now().toString(36)}_${int(rng, 0, 60)}` : undefined,
      spanId: rng() < 0.3 ? `sp_${int(rng, 100, 999)}` : undefined,
    });
  }
  return out.sort((a, b) => (a.ts < b.ts ? 1 : -1));
}

export function clusterLogs(logs: LogLine[]): LogCluster[] {
  const map = new Map<string, LogCluster>();
  for (const l of logs) {
    // Simple template extraction: replace numbers and quoted strings with placeholders.
    const pattern = l.message
      .replace(/=\d+/g, "=<num>")
      .replace(/'[^']*'/g, "<str>")
      .replace(/[a-f0-9]{8,}/g, "<hash>")
      .slice(0, 140);
    const key = `${l.level}::${pattern}`;
    const c = map.get(key);
    if (c) {
      c.count++;
      if (!c.services.includes(l.service)) c.services.push(l.service);
      if (l.ts > c.lastSeen) c.lastSeen = l.ts;
      if (l.ts < c.firstSeen) c.firstSeen = l.ts;
    } else {
      map.set(key, {
        pattern,
        count: 1,
        services: [l.service],
        sample: l.message,
        firstSeen: l.ts,
        lastSeen: l.ts,
        severity: l.level === "fatal" || l.level === "error" ? "high" : l.level === "warn" ? "medium" : "low",
      });
    }
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}
